import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAlerts } from '../api/hooks';
import type { Role } from '../api/types';
import { ROLE_LABELS, type PageKey } from '../lib/permissions';
import { applyTheme, readTheme, type ThemePref } from '../lib/theme';
import { useServices } from '../services';
import { useSession } from '../state/session';
import { AssistantPanel } from './AssistantPanel';
import {
  IconBell, IconBox, IconBuilding, IconCalendar, IconCalendarGrid, IconChannels, IconChat, IconClock, IconClose, IconHamburger,
  IconInfluencer, IconMegaphone, IconMenu, IconPrep, IconSettings, IconShield, IconTarget, IconToday, IconUpload, IconUsers,
} from './icons';

interface NavItem {
  key: PageKey;
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'This site',
    items: [
      { key: 'today', to: '/', label: 'Today', icon: IconToday },
      { key: 'daily', to: '/daily', label: 'Daily', icon: IconCalendarGrid },
      { key: 'hourly', to: '/hourly', label: 'Hourly', icon: IconClock },
      { key: 'channels', to: '/channels', label: 'Channels', icon: IconChannels },
      { key: 'products', to: '/products', label: 'Products', icon: IconBox },
      { key: 'prep', to: '/prep', label: 'Prep', icon: IconPrep },
      { key: 'menu', to: '/menu', label: 'Menu', icon: IconMenu },
      { key: 'alerts', to: '/alerts', label: 'Alerts', icon: IconBell },
      { key: 'accuracy', to: '/accuracy', label: 'Accuracy', icon: IconTarget },
    ],
  },
  {
    title: 'Plan & data',
    items: [
      { key: 'inputs', to: '/inputs', label: 'Inputs', icon: IconCalendar },
      { key: 'quiet', to: '/quiet-days', label: 'Quiet days', icon: IconMegaphone },
      { key: 'influencers', to: '/influencers', label: 'Influencers', icon: IconInfluencer },
      { key: 'upload', to: '/upload', label: 'Upload data', icon: IconUpload },
      { key: 'settings', to: '/settings', label: 'Site settings', icon: IconSettings },
    ],
  },
  {
    title: 'Group',
    items: [
      { key: 'group', to: '/group', label: 'Group view', icon: IconBuilding },
      { key: 'users', to: '/users', label: 'Users', icon: IconUsers },
      { key: 'group_settings', to: '/settings/group', label: 'Group settings', icon: IconSettings },
      { key: 'data_sharing', to: '/settings/data-sharing', label: 'Data sharing', icon: IconShield },
    ],
  },
];

function NavList({ onNavigate, openAlerts }: { onNavigate?: () => void; openAlerts?: number }) {
  const { can } = useSession();
  return (
    <nav aria-label="Main">
      {SECTIONS.map((s) => {
        const items = s.items.filter((i) => can(i.key));
        if (!items.length) return null;
        return (
          <div className="nav-group" key={s.title} style={{ marginBottom: 14 }}>
            <p className="eyebrow">{s.title}</p>
            {items.map((i) => (
              <NavLink key={i.key} to={i.to} end className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onNavigate}>
                <i.icon className="nav-icon" />
                {i.label}
                {i.key === 'alerts' && !!openAlerts && (
                  <span className="count" aria-label={`${openAlerts} open`}>{openAlerts}</span>
                )}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

function Wordmark() {
  return (
    <Link to="/" className="wordmark" aria-label="DemandPilot home">
      Demand<span>Pilot</span>
    </Link>
  );
}

function SiteSwitcher() {
  const { sites, siteId, setSiteId } = useSession();
  if (sites.length <= 1) {
    return <div className="site-switcher" style={{ fontWeight: 600 }}>{sites[0]?.name}</div>;
  }
  return (
    <div className="site-switcher">
      <label htmlFor="site-switcher" className="sr-only">Site</label>
      <select id="site-switcher" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState<ThemePref>(readTheme);
  const next: Record<ThemePref, ThemePref> = { system: 'light', light: 'dark', dark: 'system' };
  const label: Record<ThemePref, string> = { system: 'Auto', light: 'Light', dark: 'Dark' };
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={() => {
        const t = next[theme];
        applyTheme(t);
        setTheme(t);
      }}
      aria-label={`Theme: ${label[theme]}. Change theme`}
      title="Change theme"
    >
      ◐ <span className="hide-sm">{label[theme]}</span>
    </button>
  );
}

function UserMenu() {
  const { me, role } = useSession();
  const { auth, mock } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = async () => {
    await auth.signOut();
    qc.clear();
    navigate('/sign-in', { replace: true });
  };
  const setMockRole = (r: Role) => {
    try {
      localStorage.setItem('dp-mock-role', r);
    } catch {
      /* ignore */
    }
    qc.clear();
    navigate('/');
  };
  return (
    <details className="user-menu" style={{ position: 'relative' }}>
      <summary className="btn btn-ghost btn-sm" style={{ listStyle: 'none' }}>
        <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 99, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'inline-grid', placeItems: 'center', fontWeight: 600 }}>
          {me.user.name.slice(0, 1)}
        </span>
        <span className="hide-sm">{me.user.name}</span>
      </summary>
      <div className="card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 260, zIndex: 30, boxShadow: 'var(--shadow)' }}>
        <div style={{ fontWeight: 600 }}>{me.user.name}</div>
        <div className="muted small">{me.user.email}</div>
        <div className="small" style={{ marginTop: 4 }}>{ROLE_LABELS[role]} · {me.tenant.name}</div>
        {mock && (
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="mock-role" className="small">Preview as role (mock mode)</label>
            <select id="mock-role" value={role} onChange={(e) => setMockRole(e.target.value as Role)}>
              {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
        )}
        <hr className="divider" style={{ margin: '12px 0' }} />
        <button type="button" className="btn btn-sm" onClick={signOut} style={{ width: '100%' }}>
          Sign out
        </button>
      </div>
    </details>
  );
}

function BottomNav({ onMenu }: { onMenu: () => void }) {
  const { can } = useSession();
  const items: NavItem[] = [
    { key: 'today', to: '/', label: 'Today', icon: IconToday },
    { key: 'hourly', to: '/hourly', label: 'Hourly', icon: IconClock },
    can('alerts')
      ? { key: 'alerts', to: '/alerts', label: 'Alerts', icon: IconBell }
      : { key: 'products', to: '/products', label: 'Products', icon: IconBox },
  ];
  return (
    <nav className="bottom-nav" aria-label="Quick">
      {items.map((i) => (
        <NavLink key={i.key} to={i.to} end className={({ isActive }) => (isActive ? 'active' : '')}>
          <i.icon />
          {i.label}
        </NavLink>
      ))}
      <button type="button" onClick={onMenu}>
        <IconHamburger />
        More
      </button>
    </nav>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { siteId, can } = useSession();
  const { mock } = useServices();
  const [navOpen, setNavOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const location = useLocation();
  const alerts = useAlerts(siteId, 'open');
  const openAlerts = can('alerts') ? alerts.data?.length : undefined;

  useEffect(() => {
    setNavOpen(false);
    document.querySelectorAll('details.user-menu[open]').forEach((d) => d.removeAttribute('open'));
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div className="shell">
      <a href="#main" className="skip-link">Skip to content</a>
      <aside className="sidenav">
        <Wordmark />
        <NavList openAlerts={openAlerts} />
      </aside>
      <div className="main">
        {mock && <div className="mock-banner">Mock mode — sample data only; nothing is saved to a server.</div>}
        <TrialBanner />
        <header className="topbar">
          <button type="button" className="btn btn-ghost btn-icon menu-btn" aria-label="Open menu" aria-expanded={navOpen} onClick={() => setNavOpen(true)}>
            <IconHamburger />
          </button>
          <Wordmark />
          <SiteSwitcher />
          <div className="spacer" />
          <ThemeToggle />
          <UserMenu />
        </header>
        <main id="main" className="content" tabIndex={-1}>
          {children ?? <Outlet />}
        </main>
      </div>
      <div className="mobile-nav" data-open={navOpen}>
        <div className="drawer-backdrop" onClick={() => setNavOpen(false)} />
        <div className="panel" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="row-between">
            <Wordmark />
            <button type="button" className="btn btn-ghost btn-icon" aria-label="Close menu" onClick={() => setNavOpen(false)}>
              <IconClose />
            </button>
          </div>
          <NavList onNavigate={() => setNavOpen(false)} openAlerts={openAlerts} />
        </div>
      </div>
      <BottomNav onMenu={() => setNavOpen(true)} />
      {!assistantOpen && (
        <button type="button" className="btn btn-primary assistant-fab" onClick={() => setAssistantOpen(true)} aria-label="Ask DemandPilot">
          <IconChat /> <span className="hide-sm">Ask DemandPilot</span>
        </button>
      )}
      {assistantOpen && <AssistantPanel onClose={() => setAssistantOpen(false)} />}
    </div>
  );
}


const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://demandpilot.novamind.nz';

/** Days left in the free trial, with a link to plans. Hidden for paying customers. */
function TrialBanner() {
  const { me } = useSession();
  const sub = me.subscription;
  if (!sub || sub.status !== 'trial' || sub.days_left == null) return null;
  const days = sub.days_left;
  return (
    <div className="trial-banner" role="status">
      <span>{days === 0 ? 'Your free trial ends today.' : `${days} day${days === 1 ? '' : 's'} left in your free trial.`}</span>
      <a href={`${SITE_URL}/pricing.html`} target="_blank" rel="noreferrer">See plans</a>
      <a href="mailto:hello@novamind.nz?subject=DemandPilot%20subscription">Talk to us</a>
    </div>
  );
}
